import { useEffect, useState } from 'react';

/** Long enough to swallow a typed word, short enough that a pause feels like an answer. */
export const DEBOUNCE_DELAY_MS = 300;

/**
 * A value that lags behind the one it is given, settling only once the caller stops
 * changing it.
 *
 * Written for the search boxes over paged lists. The filtering happens on the server, so
 * the term is part of the request -- and a request per keystroke would mean six for
 * "Ionescu", of which five are already stale by the time they land. Hold the field's own
 * value in state and feed *this* to the query: typing stays instant, and the network sees
 * one search.
 *
 * The timer is cleared on every change and on unmount, so a component that goes away
 * mid-word never sets state on the way out.
 */
export const useDebouncedValue = <TValue>(value: TValue, delayMs: number = DEBOUNCE_DELAY_MS): TValue => {
    const [settled, setSettled] = useState<TValue>(value);

    useEffect(() => {
        const timer = setTimeout(() => setSettled(value), delayMs);

        return () => clearTimeout(timer);
    }, [value, delayMs]);

    return settled;
};
